import { CustomerRepository, CompanyRepository } from "core/repository";
import { Base2Presenter } from "core/presenter";
import { CustomerConstraint } from "core/constraint";
import { Customer } from "core/entity";
import { CustomerFields } from "core/enum";
import { customValidate } from "util";
import validate from "util/validate";
import Moment from "moment";
import _ from "lodash";

class CustomerFormCreatePresenter extends Base2Presenter {
  initialState() {
    return {
      creating: true,
      customer: null,
      loading: true,
      error: false,
      title: "Novo Cliente",
      fields: [],
      companyCustomerFields: [],
      configCustomFields: [],
      formErrors: [],
      customFieldsFormErrors: []
    };
  }

  async onStart() {
    this.enqueue({ loading: true, error: false });

    await this.loadCustomer();
    await this.getCurrentFields();

    this.enqueue({ loading: false });
  }

  async loadCustomer() {
    const customerResult = await this.getCustomer();

    if (customerResult.success) {
      const data = this.normalizeInData(customerResult.data);
      this.enqueue({ customer: data });

      if (!this.viewModel.creating) {
        const customFields = await this.getFieldsConfig();

        if (customFields.success) {
          this.enqueue({
            configCustomFields: customFields.data.customer_custom_fields
          });
          const { custom_fields } = this.viewModel.customer;

          this.viewModel.configCustomFields.forEach(field => {
            if (field.type === "date" && custom_fields[field.uuid])
              this.onFormatChange(field.uuid, "YYYY/MM/DD", "DD/MM/YYYY");
            else if (field.type === "datetime" && custom_fields[field.uuid])
              this.onFormatChange(
                field.uuid,
                "YYYY/MM/DD hh:mm",
                "DD/MM/YYYY HH:mm"
              );
            else if (field.type === "hour" && custom_fields[field.uuid])
              this.onFormatChange(field.uuid, "YYYY/MM/DD hh:mm", "HH:mm");
            else return;
          });
        }
      }
    } else {
      this.enqueue({
        error:
          "Não conseguimos carregar esse cliente! Tente novamente em alguns minutos."
      });

      await this.sendNotification({
        type: "error",
        title: "Ocorreu um erro",
        content: this.viewModel.error
      });
    }
  }

  async getFieldsConfig() {
    const currentUser = await this.mRuntime.get("currentUser");
    return await CompanyRepository.get(currentUser.company_uuid);
  }

  getCustomer() {
    return { success: true, data: new Customer() };
  }

  async getCurrentFields() {
    const company = await this.getFieldsConfig();

    if (company.success) {
      const { customer_fields } = company.data;
      const data = [...customer_fields, ...CustomerFields];
      const fields = _.uniqBy(data, "key");

      this.enqueue({ fields, companyCustomerFields: customer_fields });
    } else {
      this.sendNotification({
        type: "error",
        title: "Oooops",
        content:
          "Não foi possível encontrar os dados dessa empresa. Por favor, tente mais tarde ou entre em contato com o suporte."
      });
    }
  }

  normalizeInData(data) {
    ["name", "identification", "notes", "birth_date"]
      .filter(field => !data[field])
      .forEach(field => (data[field] = ""));

    ["email", "phone"]
      .filter(field => !data[field])
      .forEach(field => (data[field] = null));

    ["address", "custom_fields"]
      .filter(field => !data[field])
      .forEach(field => (data[field] = {}));

    [
      "zipcode",
      "state",
      "city",
      "neighborhood",
      "street",
      "number",
      "complement"
    ]
      .filter(field => !data.address[field])
      .forEach(field => (data.address[field] = ""));

    return data;
  }

  async onSendForm() {
    this.enqueue({ loading: true });

    await this.removeCustomFieldMask();

    const companyCustomerFields = this.viewModel.companyCustomerFields;
    const config = this.viewModel.configCustomFields;
    const params = this.viewModel.customer;

    const customFieldsFormErrors = customValidate.validateCustomized(
      params.custom_fields,
      config
    );
    const formErrors = this.formErrors(params);

    if (
      (companyCustomerFields.length === 0 || companyCustomerFields === null) &&
      params.name === "" &&
      params.identification === "" &&
      params.email === null
    ) {
      this.sendNotification({
        type: "error",
        title: "Oooops",
        content:
          "Preencha pelo menos um dos campos: Nome, E-mail ou Identificação"
      });
    } else if (
      Object.keys(formErrors).length === 0 &&
      !!customFieldsFormErrors.success
    ) {
      params.birth_date = Moment(params.birth_date, "DD/MM/YYYY").toISOString();

      const response = await this.callRepository(params);

      if (response.success) {
        this.sendNotification({
          type: "success",
          content: "O cliente foi salvo!"
        });
        return this.navigateTo("app.registration.customer");
      } else {
        params.birth_date = !params.birth_date
          ? ""
          : Moment(params.birth_date, "YYYY/MM/DD").format("DD/MM/YYYY");
        const error = response.data.message
          ? `\n\n- ${response.data.message}\n\n`
          : `\n\nErro - Sem internet\n\n`;
        this.sendNotification({
          type: "error",
          title: "Ooops...",
          content: `Ocorreu um problema ao salvar o cliente. ${error} Verifique também os dados informados e se os campos marcados com * estão preenchidos.`
        });
      }
    } else {
      this.enqueue({
        formErrors,
        customFieldsFormErrors: customFieldsFormErrors.error
      });

      this.sendNotification({
        type: "error",
        title: "Ooops",
        content:
          "Ocorreu um problema ao salvar o cliente. Verifique os dados informados e se os campos marcados com * estão preenchidos."
      });
    }

    this.enqueue({ loading: false });
  }

  formErrors(params) {
    const customValidation = this.viewModel.fields.reduce(
      (finalField, currentField) => ({
        ...finalField,
        [`_${currentField.key}`]: currentField
      }),
      {}
    );

    const errors = {};
    const validation = validate(
      { ...params, ...customValidation },
      CustomerConstraint.general,
      { fullMessages: false }
    );

    if (!!validation) {
      Object.entries(validation)
        .map(([field, errors]) => ({ field, content: errors.join(", ") }))
        .forEach(({ field, content }) => (errors[field] = content));
    }

    return errors;
  }

  async callRepository(params) {
    return await CustomerRepository.create(params);
  }

  async removeCustomFieldMask() {
    const { custom_fields } = this.viewModel.customer;
    const uuidFields = await this.getFieldsConfig();

    if (uuidFields.success) {
      this.enqueue({
        configCustomFields: uuidFields.data.customer_custom_fields
      });
      const results = uuidFields.data.customer_custom_fields;

      results.forEach(config => {
        if (custom_fields[config.uuid] && config.type === "phone") {
          this.enqueue(vm => ({
            customer: {
              ...vm.customer,
              custom_fields: {
                ...vm.customer.custom_fields,
                [config.uuid]: vm.customer.custom_fields[config.uuid].replace(
                  /\D/g,
                  ""
                )
              }
            }
          }));
        }
      });
    }
  }

  onFormatChange(uuidField, currentFormat, newFormat) {
    this.enqueue(vm => ({
      customer: {
        ...vm.customer,
        custom_fields: {
          ...vm.customer.custom_fields,
          [uuidField]: Moment(
            vm.customer.custom_fields[uuidField],
            currentFormat
          ).format(newFormat)
        }
      }
    }));
  }

  onChange(field, subField, value) {
    if (subField) {
      this.enqueue(vm => ({
        customer: {
          ...vm.customer,
          [field]: {
            ...vm.customer[field],
            [subField]: value
          }
        }
      }));
    } else {
      this.enqueue(vm => ({
        customer: {
          ...vm.customer,
          [field]: value
        }
      }));
    }
  }

  onGoBack() {
    this.navigateTo("app.registration.customer");
  }
}

export default CustomerFormCreatePresenter;



// WEBPACK FOOTER //
// ./src/components/app/registration/customer/form/create.presenter.js